import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Bar, BarChart, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PerformanceSummary } from '@/hooks/usePartnerAnalytics';

interface RatingDistributionChartProps {
  contractors: PerformanceSummary[];
}

export function RatingDistributionChart({ contractors }: RatingDistributionChartProps) {
  const createDistribution = (ratingKey: 'overall_rating' | 'safety_rating' | 'quality_rating' | 'reliability_rating') => {
    // Filter out contractors without this specific rating type
    const ratedContractors = contractors.filter(c => c[ratingKey] !== null && c[ratingKey] > 0);
    
    const bins = [
      { range: '0-1', min: 0, max: 1, count: 0 },
      { range: '1-2', min: 1, max: 2, count: 0 },
      { range: '2-3', min: 2, max: 3, count: 0 },
      { range: '3-4', min: 3, max: 4, count: 0 },
      { range: '4-5', min: 4, max: 5, count: 0 },
    ];

    ratedContractors.forEach(c => {
      const rating = c[ratingKey];
      const bin = bins.find(b => rating >= b.min && rating < b.max) || bins[bins.length - 1];
      bin.count++;
    });

    return bins;
  };

  const chartConfig = {
    count: {
      label: 'Contractors',
      color: 'hsl(var(--chart-1))',
    },
  };

  const calculateStats = (ratingKey: 'overall_rating' | 'safety_rating' | 'quality_rating' | 'reliability_rating') => {
    // Only include contractors with ratings for this category
    const values = contractors
      .filter(c => c[ratingKey] !== null && c[ratingKey] > 0)
      .map(c => c[ratingKey]);
    
    if (values.length === 0) {
      return { mean: 0, median: 0, stdDev: 0 };
    }
    
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const sorted = [...values].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
    const stdDev = Math.sqrt(variance);

    return { mean, median, stdDev };
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Rating Distribution Analysis</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="overall">
          <TabsList className="grid w-full grid-cols-4 h-auto p-1 bg-muted/50">
            <TabsTrigger value="overall">Overall</TabsTrigger>
            <TabsTrigger value="safety">Safety</TabsTrigger>
            <TabsTrigger value="quality">Quality</TabsTrigger>
            <TabsTrigger value="reliability">Reliability</TabsTrigger>
          </TabsList>

          {['overall', 'safety', 'quality', 'reliability'].map((type) => {
            const ratingKey = `${type}_rating` as 'overall_rating' | 'safety_rating' | 'quality_rating' | 'reliability_rating';
            const data = createDistribution(ratingKey);
            const stats = calculateStats(ratingKey);

            return (
              <TabsContent key={type} value={type} className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="text-2xl font-bold">{stats.mean.toFixed(2)}</div>
                    <div className="text-xs text-muted-foreground">Mean</div>
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="text-2xl font-bold">{stats.median.toFixed(2)}</div>
                    <div className="text-xs text-muted-foreground">Median</div>
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="text-2xl font-bold">{stats.stdDev.toFixed(2)}</div>
                    <div className="text-xs text-muted-foreground">Std Dev</div>
                  </div>
                </div>

                <ChartContainer config={chartConfig} className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="range" 
                        tick={{ fill: 'hsl(var(--foreground))' }}
                      />
                      <YAxis tick={{ fill: 'hsl(var(--foreground))' }} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Bar dataKey="count" fill="var(--color-count)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </TabsContent>
            );
          })}
        </Tabs>
      </CardContent>
    </Card>
  );
}
